import { useEffect, useId, useRef, useState } from "react";
import type { GeoAddress, GeoSuggestion } from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";

/** "3013AL" → "3013 AL" */
export const fmtPostcode = (p: string | null) => (p ? p.replace(/^(\d{4})\s*([A-Z]{2})$/i, "$1 $2").toUpperCase() : "");
/** "Weena 455, 3013 AL Rotterdam" */
export const fmtAddress = (a: Pick<GeoAddress, "street" | "houseNumber" | "postcode" | "city">) =>
  [[a.street, a.houseNumber].filter(Boolean).join(" "), [fmtPostcode(a.postcode), a.city].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");

/**
 * Adres typen met suggesties uit de BAG (PDOK, via de server). Kiezen vult straat, huisnummer,
 * postcode, plaats en locatie; vrij typen mag ook (dan zonder BAG-koppeling).
 */
export function AddressField({
  value,
  onText,
  onPick,
}: {
  value: string;
  onText: (text: string) => void;
  onPick: (a: GeoAddress) => void;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<GeoSuggestion[]>([]);
  const [active, setActive] = useState(-1);
  const [note, setNote] = useState<string | null>(null);
  const typed = useRef(false);

  useEffect(() => {
    const q = value.trim();
    if (!typed.current || q.length < 3) {
      setItems([]);
      return;
    }
    const t = setTimeout(() => {
      api<GeoSuggestion[]>(`/geo/suggest?q=${encodeURIComponent(q)}`).then(
        (r) => {
          setItems(r);
          setActive(-1);
          setOpen(true);
          setNote(r.length ? null : "Geen adres gevonden. Je kunt het ook zo laten staan.");
        },
        (e: unknown) => setNote(e instanceof ApiError ? e.message : "Adres zoeken lukt nu niet"),
      );
    }, 250);
    return () => clearTimeout(t);
  }, [value]);

  async function pick(s: GeoSuggestion) {
    setOpen(false);
    setItems([]);
    typed.current = false;
    try {
      onPick(await api<GeoAddress>(`/geo/lookup?id=${encodeURIComponent(s.id)}`));
      setNote(null);
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : "Adres ophalen lukt nu niet");
    }
  }

  const listId = `${id}-list`;
  return (
    <div className="addrfield">
      <label className="field">
        <span className="label">Adres</span>
        <input
          role="combobox"
          aria-expanded={open && items.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `${id}-o${active}` : undefined}
          autoComplete="off"
          value={value}
          placeholder="Typ straat en huisnummer of postcode, bv. Weena 455"
          onChange={(e) => {
            typed.current = true;
            onText(e.target.value);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (!items.length) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
              setActive((a) => Math.min(items.length - 1, a + 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => Math.max(0, a - 1));
            } else if (e.key === "Enter" && open && active >= 0) {
              e.preventDefault();
              void pick(items[active]!);
            } else if (e.key === "Escape") setOpen(false);
          }}
        />
      </label>
      {open && items.length > 0 && (
        <ul className="addrlist" role="listbox" id={listId} aria-label="Adressen">
          {items.map((s, k) => (
            <li
              key={s.id}
              id={`${id}-o${k}`}
              role="option"
              aria-selected={k === active}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => void pick(s)}
            >
              {s.label}
            </li>
          ))}
        </ul>
      )}
      {note && <p className="hint">{note}</p>}
    </div>
  );
}
